import DndDotsIcon from '@assets/images/icons/dnd-dots.svg?react'
import EyeCloseIcon from '@assets/images/icons/eye-close.svg?react'
import MinusIcon from '@assets/images/icons/minus-circle.svg?react'
import type { ColorItemActionsProps } from '@components/ArtBoard/ColorLibrary'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Tooltip } from '@lib/components'
import type { ID } from '@octopus-do/octopus-project-core'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import cx from 'classnames'
import type React from 'react'
import {
  type ChangeEvent,
  type CSSProperties,
  type FocusEvent,
  type KeyboardEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useSelector } from 'react-redux'

import { ColorPicker } from '../ColorPicker'
import css from './ColorItem.module.scss'

interface Props extends ColorItemActionsProps {
  id: ID
}

export const ColorItem: React.FC<Props> = ({
  id,
  disabled,
  onBlur,
  onRemoveColor,
  onColorPicker,
  onSelectColor,
  onSwitchColor,
  activeColorPicker,
}) => {
  const [value, setValue] = useState('')
  const [inputIsShown, setInputIsShown] = useState(false)

  const { title, showInLegend, hexCode } = useSelector(
    migrationProjectSelector.getProjectColorById(id)
  )

  const defaultColorId = useSelector(
    migrationProjectSelector.getProjectDefaultColorId
  )

  const defaultColor = defaultColorId === id

  const inputRef = useRef<HTMLInputElement>(null)
  const [colorPickerArea, setColorPickerArea] = useState<HTMLDivElement | null>(
    null
  )

  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id, disabled })

  // a picker left open when the item went disabled closes with it
  const showColorPicker =
    !disabled && activeColorPicker.show && activeColorPicker.id === id

  const style: CSSProperties = useMemo(() => {
    return {
      transform: CSS.Transform.toString(transform),
      transition,
      backgroundColor: 'transparent',
    }
  }, [transform, transition])

  useEffect(() => {
    inputRef.current?.focus()
  }, [inputIsShown])

  const handleOnOutsideClick = () => {
    onColorPicker({ show: false, id: '' })
  }

  const handleNameClick = () => {
    if (!disabled) {
      setInputIsShown(true)
    }
  }

  const handleKeyDown = (evt: KeyboardEvent<HTMLInputElement>) => {
    if (evt.keyCode === 13) {
      const target = evt.target as HTMLInputElement

      target.blur()
      if (!value) {
        onBlur(id, value || title)
      }
      setInputIsShown(false)
    }
  }

  const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value)
  }

  const handleOnBlur = (evt: FocusEvent<HTMLInputElement>) => {
    const value =
      evt.target.value.trim().length === 0 ? title : evt.target.value

    onBlur(id, value || title)
    setInputIsShown(false)
  }

  const handleRemoveColorClick = () => {
    if (!defaultColor) {
      onRemoveColor(id)
    }
  }

  const handleColorPickerClick = () => {
    if (!disabled) {
      onColorPicker({ show: true, id })
    }
  }

  const handleOnSetColor = (color: string) => {
    onColorPicker({ show: false, id })
    onSelectColor(id, color)
  }

  const handleSwitchColor = () => {
    onSwitchColor(id, !showInLegend)
  }

  return (
    <div
      className={cx(css.item, {
        [css.defaultColor]: defaultColor,
        [css.disabled]: disabled,
      })}
      style={style}
      ref={setNodeRef}
    >
      <div className={css.dndArea} {...attributes} {...listeners}>
        <DndDotsIcon />
      </div>

      <div
        ref={setColorPickerArea}
        className={css.colorPickArea}
        style={{ backgroundColor: hexCode }}
        onClick={handleColorPickerClick}
      />

      {inputIsShown ? (
        <input
          className={css.input}
          ref={inputRef}
          onKeyDown={handleKeyDown}
          value={value}
          onBlur={handleOnBlur}
          onChange={handleOnChange}
          disabled={disabled}
        />
      ) : (
        <Tooltip
          content="Rename"
          position="top"
          className="renameTooltip"
          tooltipClassName="renameTooltipInner"
        >
          <div className={css.colorNameArea} onClick={handleNameClick}>
            {title}
          </div>
        </Tooltip>
      )}

      <div
        className={cx(css.buttonsWrapper, {
          [css.hide]: showInLegend,
        })}
      >
        <Tooltip
          content={showInLegend ? 'Hide in legend' : 'Show in legend'}
          position="top"
          className={cx('iconTooltip', {
            notUsed: !showInLegend,
            defaultColor,
          })}
        >
          <button
            className={cx(css.btn, css.switch)}
            onClick={handleSwitchColor}
            disabled={disabled}
          >
            <EyeCloseIcon />
          </button>
        </Tooltip>

        {!defaultColor && (
          <Tooltip
            content="Remove"
            position="top"
            className={cx('iconTooltip', 'removeIconTooltip', {
              hide: !showInLegend,
            })}
          >
            <button
              className={cx(css.btn, css.remove)}
              onClick={handleRemoveColorClick}
              disabled={disabled}
            >
              <MinusIcon />
            </button>
          </Tooltip>
        )}
      </div>

      {defaultColor && <div className={css.defaultColorLabel}>Default</div>}

      {showColorPicker && (
        <ColorPicker
          colorHex={hexCode}
          onOutsideClick={handleOnOutsideClick}
          onSetColor={handleOnSetColor}
          colorPikerAreaRef={colorPickerArea}
        />
      )}
    </div>
  )
}
